import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, Search, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useHaptics } from '@/hooks/useHaptics';
import { toast } from 'sonner';

interface ProcessedSuggestion {
  id: string;
  recipeName: string;
  reasoning: string;
  mealType: 'lunch' | 'dinner';
  isFromCollection: boolean;
  recipeId: string | null;
  existingRecipe?: any;
  similarMatches: any[];
  exactMatch?: any;
  newRecipeDetails?: {
    searchTerms: string;
  };
}

interface CompactMealSuggestionProps {
  suggestion: ProcessedSuggestion;
  onAccept: () => void;
  onDismiss: () => void;
  isAccepting: boolean;
  onAcceptSimilar?: (recipeId: string) => void;
}

const CompactMealSuggestion: React.FC<CompactMealSuggestionProps> = ({
  suggestion,
  onAccept,
  onDismiss,
  isAccepting,
  onAcceptSimilar
}) => {
  const [showSimilar, setShowSimilar] = useState(false);
  const navigate = useNavigate();
  const haptics = useHaptics();

  const hasExistingRecipe = suggestion.isFromCollection || suggestion.existingRecipe;
  const hasSimilarRecipes = suggestion.similarMatches.length > 0;

  const handleAccept = async () => {
    await haptics.lightImpact();
    onAccept();
  };

  const handleFindRecipe = () => {
    const searchTerms = suggestion.newRecipeDetails?.searchTerms || suggestion.recipeName;
    navigate(`/recipe-finder?q=${encodeURIComponent(searchTerms)}`);
  };

  const handleDismiss = async () => {
    await haptics.lightImpact();
    onDismiss();
    toast.info('Suggestion dismissed');
  };

  const handleAcceptSimilar = async (similarRecipe: any) => {
    await haptics.lightImpact();
    if (onAcceptSimilar) {
      onAcceptSimilar(similarRecipe.recipe.id);
    } else {
      toast.success(`Added ${similarRecipe.recipe.title} to meal plan`);
    }
  };

  const handleRecipeClick = async () => {
    if (hasExistingRecipe && (suggestion.existingRecipe || suggestion.exactMatch)) {
      await haptics.lightImpact();
      const recipe = suggestion.existingRecipe || suggestion.exactMatch;
      navigate(`/recipe/${recipe.id}`);
    }
  };

  const handleSimilarRecipeClick = async (similarRecipe: any) => {
    await haptics.lightImpact();
    navigate(`/recipe/${similarRecipe.recipe.id}`);
  };

  const handleViewDetails = async () => {
    await haptics.lightImpact();
    navigate('/meal-suggestions');
  };

  return (
    <Card className="p-3 bg-muted/30 border-primary/20 w-full max-w-full overflow-hidden">
      <div className="flex items-start gap-2 min-w-0">
        <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-xs text-muted-foreground mb-1">{suggestion.mealType.charAt(0).toUpperCase() + suggestion.mealType.slice(1)} Suggestion</p>
              {hasExistingRecipe ? (
                <button
                  onClick={handleRecipeClick}
                  className="font-medium text-sm truncate text-primary hover:text-primary/80 transition-colors text-left w-full block max-w-full"
                >
                  {suggestion.recipeName}
                </button>
              ) : (
                <p className="font-medium text-sm truncate max-w-full">{suggestion.recipeName}</p>
              )}
              <p className="text-xs text-muted-foreground line-clamp-1 max-w-full">{suggestion.reasoning}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 shrink-0"
              onClick={handleDismiss}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          <div className="flex items-center gap-2 mt-2 flex-wrap min-w-0">
            {hasExistingRecipe ? (
              <Button
                size="sm"
                className="h-7 text-xs"
                onClick={handleAccept}
                disabled={isAccepting}
              >
                {isAccepting ? 'Adding...' : 'Accept'}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={handleFindRecipe}
              >
                <Search className="h-3 w-3 mr-1" />
                Find Recipe
              </Button>
            )}

            {hasSimilarRecipes && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowSimilar(!showSimilar)}
              >
                {suggestion.similarMatches.length} similar
                {showSimilar ? (
                  <ChevronUp className="h-3 w-3 ml-1" />
                ) : (
                  <ChevronDown className="h-3 w-3 ml-1" />
                )}
              </Button>
            )}
            
            <button
              onClick={handleViewDetails}
              className="text-xs text-muted-foreground hover:text-foreground hover:underline transition-colors ml-1"
            >
              View Details
            </button>
          </div>

          {showSimilar && hasSimilarRecipes && (
            <div className="mt-2 pt-2 border-t border-border/50">
              {suggestion.similarMatches.slice(0, 2).map((match) => (
                <div
                  key={match.recipe.id}
                  className="flex items-center justify-between py-1"
                >
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <button
                      onClick={() => handleSimilarRecipeClick(match)}
                      className="text-xs font-medium truncate text-primary hover:text-primary/80 transition-colors text-left w-full block max-w-full"
                    >
                      {match.recipe.title}
                    </button>
                    <p className="text-xs text-muted-foreground">Similar recipe</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-xs ml-2"
                    onClick={() => handleAcceptSimilar(match)}
                  >
                    Add
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default CompactMealSuggestion;